// Umami web analytics (KD-4017): one self-hosted instance tracks DMC and TO on
// staging and prod. Cookieless and keeps nothing in the browser, so no consent
// banner is involved. Page views of the SPA need nothing here: the tracker
// hooks history.pushState/replaceState itself.

// The tracker looks this callback up by name on window (data-before-send).
const BEFORE_SEND = 'koobUmamiBeforeSend';

// Routes whose path segment after the prefix is a credential: whoever holds
// the URL gets in. Never send them as is.
const SECRET_SEGMENTS = [
  [/^\/hotelkeeper\/[^/]+/, '/hotelkeeper/:secret'],
  [/^\/travel-diary\/[^/]+/, '/travel-diary/:shareToken'],
];

function redactPath(pathname) {
  return SECRET_SEGMENTS.reduce(
    (path, [pattern, replacement]) => path.replace(pattern, replacement),
    pathname,
  );
}

// Payload URLs come either absolute (url) or, for a same-origin referrer,
// as a bare path. Anything that is not ours goes through untouched.
export function redactUrl(value) {
  if (!value) return value;

  if (value.startsWith('/')) return redactPath(value);

  try {
    const url = new URL(value);
    if (url.origin !== window.location.origin) return value;
    url.pathname = redactPath(url.pathname);
    return url.toString();
  } catch {
    return value;
  }
}

export function redactPayload(_type, payload) {
  return {
    ...payload,
    url: redactUrl(payload.url),
    referrer: redactUrl(payload.referrer),
  };
}

// The session recorder sends the page address as is (location.href), with no
// hook to clean it. It loads only on a page whose address carries no secret:
// one opened from a link with a token (impersonation, password reset, email
// change, hotel keeper, shared travel diary) stays unrecorded.
// ponytail: decided once per page load; an in-app move to such an address
// would still be recorded. None exists today: these links all open a new page.
export function isSafeToRecord({ pathname, search, hash }) {
  return !search && !hash && redactPath(pathname) === pathname;
}

function addScript(src, websiteId, dataset = {}) {
  const script = document.createElement('script');
  // Injected scripts run as soon as they load; this keeps them in order, so
  // the recorder finds the tracker's session.
  script.async = false;
  script.src = src;
  Object.assign(script.dataset, { websiteId, ...dataset });
  document.head.appendChild(script);
}

let isInitialized = false;

// Loads the tracker, and the session recorder next to it, when the build
// carries both values. Builds without them (local, dev, tickets, e2e) track
// nothing. Whether the recorder records is set per website in Umami.
export function initUmami({ scriptUrl, websiteId }) {
  if (!scriptUrl || !websiteId || isInitialized) return;

  isInitialized = true;

  window[BEFORE_SEND] = redactPayload;

  addScript(scriptUrl, websiteId, {
    beforeSend: BEFORE_SEND,
    // Query strings carry one-time tokens (password reset, impersonation,
    // OAuth codes); hashes carry nothing worth counting.
    excludeSearch: 'true',
    excludeHash: 'true',
  });

  if (isSafeToRecord(window.location)) {
    addScript(new URL('recorder.js', scriptUrl).href, websiteId);
  }
}
